import { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Box, Typography, CircularProgress, LinearProgress } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import { getScannerCategoryDetail } from 'services/scanner-service';
import { Select } from 'Components/ui';
import { resolveCssVar } from 'utils/css_var_utils';

const T = {
  bg: 'var(--vo-white)',
  surface: 'var(--vo-mist)',
  border: 'var(--vo-line)',
  text: 'var(--vo-ink)',
  muted: 'var(--vo-slate)',
  dim: 'var(--vo-steel)',
};

const SEV_CFG = [
  { key: 'critical',      label: 'Critical',  color: 'var(--vo-sev-critical)' },
  { key: 'high',          label: 'High',      color: 'var(--vo-sev-high)' },
  { key: 'medium',        label: 'Medium',    color: 'var(--vo-sev-medium)' },
  { key: 'low',           label: 'Low',       color: 'var(--vo-sev-low)' },
  { key: 'informational', label: 'Info',      color: 'var(--vo-sev-info)' },
];

export function RiskRadar({ categories = [], isLoading, slug }) {
  const { t } = useTranslation(['scanner']);
  const [selectedCat, setSelectedCat] = useState('');

  const selectedIdx = useMemo(
    () => categories.findIndex(c => c.category === selectedCat),
    [categories, selectedCat]
  );

  // Fetch severity detail when a category is selected
  const { data: detailData, isFetching: detailLoading } = useQuery(
    ['scannerCategoryDetail', slug, selectedCat],
    () => getScannerCategoryDetail(slug, selectedCat).then(r => r.data),
    { enabled: !!slug && !!selectedCat, staleTime: 5 * 60 * 1000, keepPreviousData: true }
  );

  const maxVal = useMemo(
    () => Math.max(...categories.map(c => c.failCount), 1),
    [categories]
  );

  const option = useMemo(() => {
    const indicator = categories.map(c => ({
      name: c.label,
      max: Math.ceil(maxVal * 1.15) || 1,
    }));

    const baseValues = categories.map(c => c.failCount);

    const series = [
      {
        type: 'radar',
        data: [{
          value: baseValues,
          name: t('scanner:dashboard.radarFail', 'Fail'),
          areaStyle: { color: resolveCssVar('color-mix(in srgb, var(--vo-purple) 22%, transparent)') },
          lineStyle: { color: resolveCssVar('var(--vo-purple)'), width: 2 },
          itemStyle: { color: resolveCssVar('var(--vo-purple)'), borderWidth: 2 },
          symbol: 'circle',
          symbolSize: 5,
        }],
      },
    ];

    // Cyan dot overlay for selected category
    if (selectedIdx >= 0) {
      series.push({
        type: 'radar',
        data: [{
          value: categories.map((_, i) => i === selectedIdx ? categories[i].failCount : null),
          lineStyle: { opacity: 0 },
          areaStyle: { opacity: 0 },
          itemStyle: { color: resolveCssVar('var(--vo-celeste)'), borderColor: resolveCssVar('var(--vo-celeste)'), borderWidth: 2 },
          symbol: 'circle',
          symbolSize: 9,
        }],
      });
    }

    return {
      backgroundColor: 'transparent',
      tooltip: { show: false },
      radar: {
        indicator,
        shape: 'polygon',
        splitNumber: 4,
        center: ['50%', '55%'],
        radius: '73%',
        axisName: {
          color: resolveCssVar(T.muted),
          fontSize: 11,
          fontFamily: 'inherit',
        },
        splitLine: { lineStyle: { color: resolveCssVar(T.border), width: 1 } },
        splitArea: { show: false },
        axisLine: { lineStyle: { color: resolveCssVar(T.border), width: 1 } },
      },
      series,
    };
  }, [categories, maxVal, selectedIdx, t]);

  const detail = detailData ?? null;
  const totalFail = detail?.totalFail ?? 0;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 420 }}>
        <CircularProgress size={28} sx={{ color: 'var(--vo-purple)' }} />
      </Box>
    );
  }

  if (categories.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 420 }}>
        <Typography sx={{ color: T.dim, fontSize: '0.9rem' }}>{t('scanner:dashboard.noData', 'No data')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', minHeight: 540 }}>
      {/* ── Left: radar chart ──────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
        {/* Category selector */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2.5, pt: 2, pb: 0 }}>
          <Select
            name="riskRadarCategory"
            value={selectedCat}
            onChange={setSelectedCat}
            fullWidth={false}
            sx={{
              minWidth: 160,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: T.border },
              '& .MuiSvgIcon-root': { color: T.muted },
              // height/fontSize live here (not top-level) because Select's
              // own CLOSED_SX explicitly sets both on this exact slot —
              // a top-level override only reaches the outer root, which
              // loses to the child's own explicit value.
              '& .MuiSelect-select': { height: '32px', py: '4px', pr: '28px !important', color: T.text, fontSize: '0.78rem' },
              backgroundColor: 'var(--vo-white)',
              borderRadius: 1.5,
            }}
            options={[
              { value: '', label: t('scanner:dashboard.allCategories', 'All categories') },
              ...categories.map(c => ({ value: c.category, label: c.label })),
            ]}
          />
        </Box>

        <ReactECharts
          option={option}
          style={{ height: 500, width: '100%' }}
          notMerge
          onEvents={{
            click: (params) => {
              const idx = typeof params.indicatorIndex === 'number'
                ? params.indicatorIndex
                : typeof params.dataIndex === 'number' ? params.dataIndex : -1;
              if (idx >= 0 && categories[idx]) {
                const cat = categories[idx].category;
                setSelectedCat(prev => (prev === cat ? '' : cat));
              }
            },
          }}
        />
      </Box>

      {/* ── Right: detail panel ─────────────────────────────────────────────── */}
      <Box sx={{
        width: 220,
        flexShrink: 0,
        borderLeft: `1px solid ${T.border}`,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        px: 2.5,
        py: 3,
        backgroundColor: T.surface,
      }}>
        {!selectedCat ? (
          <Typography sx={{ color: T.dim, fontSize: '0.8rem', textAlign: 'center' }}>
            {t('scanner:dashboard.selectCategory', 'Select a category')}
          </Typography>
        ) : detailLoading && !detail ? (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={22} sx={{ color: 'var(--vo-purple)' }} />
          </Box>
        ) : detail ? (
          <>
            <Typography sx={{ color: T.text, fontWeight: 700, fontSize: '0.9rem', mb: 0.4 }}>
              {detail.label}
            </Typography>
            <Typography sx={{ color: T.muted, fontSize: '0.72rem', mb: 2.5 }}>
              {totalFail.toLocaleString()} {t('scanner:dashboard.totalFindings', 'Total Findings')}
            </Typography>

            {SEV_CFG.map(({ key, label, color }) => {
              const count = detail.severities?.[key] ?? 0;
              const pct = totalFail > 0 ? Math.round((count / totalFail) * 100) : 0;
              return (
                <Box key={key} sx={{ mb: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography sx={{ color: T.muted, fontSize: '0.75rem', width: 56 }}>{label}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography sx={{ color: T.text, fontSize: '0.72rem', fontWeight: 600, minWidth: 28, textAlign: 'right' }}>{pct}%</Typography>
                      <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
                      <Typography sx={{ color, fontSize: '0.72rem', fontWeight: 700, minWidth: 24 }}>{count.toLocaleString()}</Typography>
                    </Box>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={pct}
                    sx={{
                      height: 6, borderRadius: 3,
                      backgroundColor: 'var(--vo-line)',
                      '& .MuiLinearProgress-bar': { backgroundColor: color, borderRadius: 3 },
                    }}
                  />
                </Box>
              );
            })}
          </>
        ) : null}
      </Box>
    </Box>
  );
}
